import axios from "axios";

/**
 * Resolve the backend URL from environment.
 *
 * We deliberately fail fast (in development) when REACT_APP_BACKEND_URL is
 * missing or blank instead of silently producing "undefined/api" as the
 * axios baseURL — the latter used to generate confusing "Network unavailable"
 * errors that looked like the backend was down.
 */
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || "").trim();

const isMissing =
  !rawBackendUrl || rawBackendUrl === "undefined" || rawBackendUrl === "null";

if (isMissing && typeof window !== "undefined") {
  // eslint-disable-next-line no-console
  console.error(
    "[api] REACT_APP_BACKEND_URL is not set. Create frontend/.env from "
      + ".env.example (REACT_APP_BACKEND_URL=http://localhost:8000) and "
      + "restart `yarn start`. All backend calls will fail until this is set.",
  );
}

// Trim any trailing slashes so we can safely append /api.
const BACKEND_URL = isMissing
  ? ""
  : rawBackendUrl.replace(/\/+$/, "");

export const API = isMissing ? "" : `${BACKEND_URL}/api`;
export { BACKEND_URL };

// Axios instance that always sends cookies (session_token httpOnly).
export const http = axios.create({
  baseURL: API,
  withCredentials: true,
});

// If the backend URL is missing, reject every request up-front with a
// well-typed error so callers can render a clear message instead of a
// misleading axios ERR_NETWORK.
if (isMissing) {
  http.interceptors.request.use(() => {
    const err = new Error(
      "Backend URL is not configured (REACT_APP_BACKEND_URL).",
    );
    err.code = "ERR_BACKEND_URL_MISSING";
    return Promise.reject(err);
  });
}

http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      error.isAuthError = true;
    }
    return Promise.reject(error);
  }
);

export const listActivities = async () => (await http.get(`/activities`)).data;
export const listComparisonCandidates = async () =>
  (await http.get(`/activities`, { params: { comparison_candidates: true } })).data;
export const getActivitySummary = async (sport = "all") =>
  (
    await http.get(`/activities/summary`, {
      params: sport && sport !== "all" ? { sport } : undefined,
    })
  ).data;
export const listActivitiesInRange = async ({ startTime, endTime }, options = {}) =>
  (await http.get(`/activities`, {
    params: { start_time: startTime, end_time: endTime },
    ...options,
  })).data;
export const getActivity = async (id) => (await http.get(`/activities/${id}`)).data;
export const getActivityExecutionAnalysis = async (id) =>
  (await http.get(`/activities/${id}/execution-analysis`)).data;
export const compareActivities = async (leftId, rightId) =>
  (await http.get(`/activities/compare`, { params: { left_id: leftId, right_id: rightId } })).data;
export const applyActivityCalendarChange = async (id, payload) =>
  (await http.post(`/activities/${id}/calendar-change`, payload)).data;
export const getActivitySegmentStats = async (id, params, options = {}) =>
  (await http.get(`/activities/${id}/segment-stats`, { params, ...options })).data;
export const deleteActivity = async (id) => (await http.delete(`/activities/${id}`)).data;
export const updateActivity = async (id, payload) =>
  (await http.patch(`/activities/${id}`, payload)).data;
export const fetchProgress = async (params) => {
  const response = await http.get(`/progress`, { params, timeout: 15000 });
  return { status: response.status, data: response.data };
};
export const fetchProgressCalculationStatus = async () =>
  (await http.get(`/progress/calculation-status`, { timeout: 10000 })).data;
export const fetchAerobicFitness = async (params) =>
  (await http.get(`/aerobic-fitness`, { params, timeout: 10000 })).data;
export const createMeasuredVo2 = async (payload) =>
  (await http.post(`/aerobic-fitness/measurements`, payload)).data;
export const correctMeasuredVo2 = async (id, payload) =>
  (await http.post(`/aerobic-fitness/measurements/${id}/corrections`, payload)).data;
export const deactivateMeasuredVo2 = async (id) =>
  (await http.delete(`/aerobic-fitness/measurements/${id}`)).data;

export const listMetricHistory = async (params) =>
  (await http.get(`/metric-history`, { params })).data;
export const createMetricHistory = async (payload) =>
  (await http.post(`/metric-history`, payload)).data;
export const updateMetricHistory = async (id, payload) =>
  (await http.patch(`/metric-history/${id}`, payload)).data;
export const deleteMetricHistory = async (id) =>
  (await http.delete(`/metric-history/${id}`)).data;

export const uploadActivity = async (file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await http.post(`/activities/upload`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const uploadActivityBatch = async (files) => {
  const form = new FormData();
  for (const file of files) {
    form.append("files", file);
  }
  const { data } = await http.post(`/activities/upload-batch`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

// Auth
export const authMe = async () => (await http.get(`/auth/me`, { timeout: 10000 })).data;
export const fetchAdminStats = async () => (await http.get(`/admin/stats`, { timeout: 10000 })).data;
export const authDevLogin = async () => (await http.post(`/auth/dev-login`, {}, { timeout: 10000 })).data;
export const authGoogleConnectUrl = () => `${BACKEND_URL}/api/auth/google/connect`;
export const authLogout = async () => (await http.post(`/auth/logout`)).data;
export const authDeleteAccount = async ({ email, confirmation }) =>
  (await http.post(`/auth/account/delete`, { email, confirmation })).data;
export const authRequestAccountDeletionCode = async () =>
  (await http.post(`/auth/account/delete/request-code`, {})).data;
export const authVerifyAccountDeletionCode = async ({ code }) =>
  (await http.post(`/auth/account/delete/verify-code`, { code })).data;
export const authUpdateProfile = async (payload) =>
  (await http.patch(`/auth/profile`, payload)).data;

// Athlete profile (Chart 1 canonical authority — includes IANA timezone).
export const getAthleteProfile = async () =>
  (await http.get(`/athlete/profile`)).data;
export const createAthleteProfile = async (payload) =>
  (await http.post(`/athlete/profile`, payload)).data;
export const patchAthleteProfile = async (payload) =>
  (await http.patch(`/athlete/profile`, payload)).data;

// Strava integration
export const stravaConfig = async () => (await http.get(`/integrations/strava/config`)).data;
export const stravaConnect = async () => (await http.get(`/integrations/strava/connect`)).data;
export const stravaStatus = async () => (await http.get(`/integrations/strava/status`)).data;
export const stravaSync = async () => (await http.post(`/integrations/strava/sync`)).data;
export const stravaDisconnect = async () => (await http.post(`/integrations/strava/disconnect`)).data;

// Legacy aliases (deprecated)
export const stravaAuthorizeUrl = stravaConnect;
export const stravaExchange = async () => {
  throw new Error("Server-side OAuth callback replaces client exchange");
};

// Progress
export const progressSummary = async (params) =>
  (await http.get(`/progress/summary`, { params })).data;
export const progressMetricHistory = async (params) =>
  (await http.get(`/progress/metric-history`, { params })).data;
export const progressBestEfforts = async (params) =>
  (await http.get(`/progress/best-efforts`, { params })).data;
export const progressZoneHistory = async (params) =>
  (await http.get(`/progress/zone-history`, { params })).data;
export const progressCoverage = async (params) =>
  (await http.get(`/progress/coverage`, { params })).data;
export const progressSeries = async (params) =>
  (await http.get(`/progress/series`, { params })).data;
